import {
  FloatingTree,
  useDismiss,
  useFloatingParentNodeId,
  type FloatingRootContext,
} from '@floating-ui/react';
import { type ReactNode } from 'react';

/**
 * Package-level contract for Escape priority between nested floating
 * components (`Modal`, `Select`, `Menu`, `Popover`, and any future one):
 * whichever opened most recently — a `Select` opened inside a `Modal` —
 * handles Escape first, without also closing its ancestor. This is derived
 * from actual React nesting (via `@floating-ui/react`'s `FloatingTree`), not
 * from a value a caller has to look up or thread through props.
 *
 * Every EDS floating component should compose from these two primitives
 * instead of hand-rolling `FloatingTree`/`bubbles` wiring per component —
 * that duplication is what let one component regress the priority order
 * (a `bubbles: true` typo silently made it defer to its *ancestor* instead
 * of the other way around) without any other component's behavior changing
 * to show it.
 */

/**
 * Wrap a floating component's rendered output in this, once, near its root.
 * Creates a new `FloatingTree` only when there's no ambient one (i.e. this
 * instance is the outermost floating layer on the page right now) — so a
 * `Select`/`Menu`/`Popover` opened inside a `Modal` joins the `Modal`'s tree
 * automatically, and the same component used standalone elsewhere gets its
 * own tree with no other participants. No id has to be threaded through
 * consumer code either way.
 */
export function FloatingPriorityRoot({ children }: { children: ReactNode }) {
  const parentId = useFloatingParentNodeId();

  if (parentId == null) {
    return <FloatingTree>{children}</FloatingTree>;
  }

  return children;
}

/**
 * `useDismiss` with Escape priority pinned to "defer to whichever floating
 * element opened more recently" — the one correct value for every EDS
 * floating component, so it's the default instead of something each
 * component's author has to remember and get right individually.
 */
export function useEscapePriorityDismiss(
  context: FloatingRootContext,
  options?: Omit<Parameters<typeof useDismiss>[1], 'bubbles'>,
) {
  return useDismiss(context, { ...options, bubbles: { escapeKey: false } });
}
